
import { useState, useEffect, useCallback } from 'react';

interface NavbarProps {
  onOpenModal: () => void;
}

/**
 * Navbar component with scroll‑aware styling.
 * Added defensive checks and extracted repeated class logic for clarity
 * and to avoid parsing issues with Tailwind arbitrary values.
 */
export default function Navbar({ onOpenModal }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);

  // Memoised scroll handler to avoid re‑creating the function on every render
  const handleScroll = useCallback(() => {
    // Guard against environments where `window` might be undefined (e.g., SSR)
    if (typeof window !== 'undefined') {
      setScrolled(window.scrollY > 50);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    window.addEventListener('scroll', handleScroll);
    // Initialise state in case the page is already scrolled
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  // Centralised class for the navigation links – avoids inline template literals
  const linkBaseClass =
    'text-sm uppercase tracking-wider font-medium transition-colors cursor-pointer';
  const linkColorClass = scrolled
    ? 'text-[#0B1F3A]/70 hover:text-[#0B1F3A]'
    : 'text-white/70 hover:text-white';

  const navBaseClass =
    'fixed top-0 left-0 right-0 z-50 transition-all duration-300';
  const navScrolledClass = scrolled ? 'bg-white shadow-sm' : 'bg-transparent';

  return (
    <nav className={`${navBaseClass} ${navScrolledClass}`}>
      <div className="max-w-[1440px] mx-auto px-8 lg:px-20 py-5 flex items-center justify-between">
        <div className="flex items-center">
          <img
            src="https://static.readdy.ai/image/0fc33fa68f46f743570ad8ba1c3172ea/28e3f7ab3ff0ad2244f11888b610f407.png"
            alt="Scale North Solutions"
            className="h-10"
          />
        </div>

        <div className="flex items-center gap-10">
          <a href="#services" className={`${linkBaseClass} ${linkColorClass}`}>
            Services
          </a>
          <a href="#approach" className={`${linkBaseClass} ${linkColorClass}`}>
            Approach
          </a>
          <a href="#industries" className={`${linkBaseClass} ${linkColorClass}`}>
            Industries
          </a>
          <a href="#contact" className={`${linkBaseClass} ${linkColorClass}`}>
            Contact
          </a>

          <button
            onClick={onOpenModal}
            className="bg-[#1EB6B0] text-white px-8 h-11 rounded-md text-sm font-medium hover:bg-[#1EB6B0]/90 transition-colors whitespace-nowrap cursor-pointer"
          >
            Request Consultation
          </button>
        </div>
      </div>
    </nav>
  );
}
